import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
  Paperclip,
  Maximize2,
  Pencil,
  Phone,
  Plus,
  Search,
  Stethoscope,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import ChoixFichier, { type PoigneeFichier } from '../components/ChoixFichier';
import EquipementsAdherent from '../components/EquipementsAdherent';
import EquipesClub from '../components/EquipesClub';
import InventaireEquipements from '../components/InventaireEquipements';
import LicenceAdherent from '../components/LicenceAdherent';
import TableauCotisations from '../components/TableauCotisations';
import {
  createAdherent,
  deleteAdherent,
  deposerDocumentAdherent,
  fetchAdherent,
  fetchAdherents,
  supprimerDocumentAdherent,
  updateAdherent,
  updateContactsAdherent,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

type Notice = { type: 'success' | 'error'; message: string } | null;

type Onglet = 'repertoire' | 'cotisations' | 'equipements' | 'equipes';

/** Le menu conduit à chaque écran : le titre dit lequel on regarde. */
const TITRES: Record<Onglet, { titre: string; aide: string }> = {
  repertoire: {
    titre: 'Répertoire',
    aide: "Le registre du club : état civil, contacts d'urgence et pièces au dossier.",
  },
  cotisations: {
    titre: 'Licences et cotisations',
    aide: 'Qui doit encore, combien, et depuis quand.',
  },
  equipements: {
    titre: 'Équipements',
    aide: 'Ce que le club a fourni, prêté ou vendu, et ce qu\'il attend en retour.',
  },
  equipes: {
    titre: 'Équipes',
    aide: 'Les groupes engagés cette saison, et la feuille qu\'on emporte le dimanche.',
  },
};

const formInitial = {
  civilite: '',
  prenom: '',
  nom: '',
  dateNaissance: '',
  lieuNaissance: '',
  nationalite: '',
  sexe: '',
  email: '',
  telephone: '',
  adresse: '',
  codePostal: '',
  ville: '',
  discipline: '',
  commentaire: '',
};

/** Les quatre contacts que le club doit pouvoir appeler un soir de match. */
const contactsInitiaux = [
  { role: 'parent1', nom: '', lien: '', telephone: '', email: '', remarque: '' },
  { role: 'parent2', nom: '', lien: '', telephone: '', email: '', remarque: '' },
  { role: 'medecin', nom: '', lien: '', telephone: '', email: '', remarque: '' },
  { role: 'autre', nom: '', lien: '', telephone: '', email: '', remarque: '' },
];

const libelleRole: Record<string, string> = {
  parent1: 'Responsable légal 1',
  parent2: 'Responsable légal 2',
  medecin: 'Médecin',
  autre: 'Autre personne',
};

/**
 * Les liens de parenté, proposés plutôt que devinés.
 *
 * « mère », « Mère », « maman » et « M » désignent la même chose et rendent
 * tout tri impossible. La liste vient du serveur ; celle-ci n'est qu'un repli
 * le temps qu'elle arrive.
 */
const liensParDefaut: Record<string, string> = {
  mere: 'Mère',
  pere: 'Père',
  belle_mere: 'Belle-mère',
  beau_pere: 'Beau-père',
  grand_mere: 'Grand-mère',
  grand_pere: 'Grand-père',
  tante: 'Tante',
  oncle: 'Oncle',
  soeur: 'Sœur',
  frere: 'Frère',
  tuteur: 'Tuteur légal',
  famille_accueil: "Famille d'accueil",
  autre: 'Autre',
};

/** Les initiales, quand la fiche n'a pas de photo. */
function initiales(prenom: string, nom: string): string {
  return `${(prenom[0] ?? '').toUpperCase()}${(nom[0] ?? '').toUpperCase()}` || '?';
}

/**
 * Le registre des adhérents.
 *
 * Le bureau y cherche quelqu'un avant tout : la barre de recherche est donc
 * en tête et mord dès deux lettres — en dessous, la liste entière reste
 * affichée plutôt que de clignoter à chaque frappe. La fiche s'ouvre à côté
 * de la liste, pas à sa place : on compare deux adhérents sans perdre sa
 * recherche.
 */
export default function AdherentsPage({ onglet = 'repertoire' }: { onglet?: Onglet }) {
  const { aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peutGerer = aLeDroit('gerer_adherents');

  const [recherche, setRecherche] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [inactifs, setInactifs] = useState(false);
  /*
    L'ADHÉRENT VISÉ PAR L'ADRESSE, S'IL Y EN A UN.

    La fiche complète renvoie ici pour modifier — « /adherents?fiche=42 ».
    Sans cette lecture, le bouton « Modifier » de la fiche ramènerait à une
    liste sans sélection, et il faudrait retrouver la personne à la main :
    exactement le geste qu'on venait d'éviter.

    Lu une seule fois, au départ. Le tenir synchronisé ferait sauter la
    sélection dès qu'on clique sur un autre nom, puisque l'adresse, elle, ne
    change pas.
  */
  const [parametres] = useSearchParams();
  const vise = Number(parametres.get('fiche'));

  const [selection, setSelection] = useState<number | null>(
    Number.isFinite(vise) && vise > 0 ? vise : null,
  );
  const [notice, setNotice] = useState<Notice>(null);
  const [formOuvert, setFormOuvert] = useState(false);
  const [form, setForm] = useState(formInitial);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [contacts, setContacts] = useState<any[] | null>(null);
  const fichierRef = useRef<PoigneeFichier>(null);
  const [typeDocument, setTypeDocument] = useState('');
  const [libelleDocument, setLibelleDocument] = useState('');
  const [refusDepot, setRefusDepot] = useState<string | null>(null);

  const annoncer = (type: 'success' | 'error', message: string) => {
    setNotice({ type, message });
    if (type === 'success') setTimeout(() => setNotice(null), 3000);
  };

  // La recherche ne part au serveur qu'à partir de deux caractères : en
  // dessous, on montre le registre entier plutôt qu'un filtrage inutile.
  const requeteRecherche = recherche.trim().length >= 2 ? recherche.trim() : '';

  const registre = useQuery({
    queryKey: ['adherents', requeteRecherche, discipline, inactifs],
    queryFn: () => fetchAdherents({ q: requeteRecherche, discipline, inactifs }),
  });

  const fiche = useQuery({
    queryKey: ['adherent', selection],
    queryFn: () => fetchAdherent(selection as number),
    enabled: selection !== null,
  });

  const config = registre.data?.configuration;
  const liens: Record<string, string> = registre.data?.vocabulaire?.liens ?? liensParDefaut;
  const adherents: any[] = registre.data?.adherents ?? [];
  const detail = fiche.data?.adherent;

  const rafraichir = () => {
    queryClient.invalidateQueries({ queryKey: ['adherents'] });
    if (selection !== null) queryClient.invalidateQueries({ queryKey: ['adherent', selection] });
  };

  const enregistrer = useMutation({
    mutationFn: (donnees: Record<string, unknown>) =>
      enEdition !== null ? updateAdherent(enEdition, donnees) : createAdherent(donnees),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      setFormOuvert(false);
      setForm(formInitial);
      setEnEdition(null);
      rafraichir();
      if (d.adherent?.id) setSelection(d.adherent.id);
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const retirer = useMutation({
    mutationFn: (id: number) => deleteAdherent(id),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      setSelection(null);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const enregistrerContacts = useMutation({
    mutationFn: (liste: any[]) => updateContactsAdherent(selection as number, liste),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      setContacts(null);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const deposer = useMutation({
    mutationFn: (formulaire: FormData) => deposerDocumentAdherent(selection as number, formulaire),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      fichierRef.current?.vider();
      setLibelleDocument('');
      setTypeDocument('');
      setRefusDepot(null);
      rafraichir();
    },
    // Le refus du serveur se dit a cote du bouton, pas en haut de page :
    // c'est la qu'on regarde apres avoir clique.
    onError: (e: Error) => setRefusDepot(e.message),
  });

  const retirerDocument = useMutation({
    mutationFn: (id: number) => supprimerDocumentAdherent(id),
    onSuccess: (d: any) => {
      annoncer('success', d.message);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  const ouvrirCreation = () => {
    setForm(formInitial);
    setEnEdition(null);
    setFormOuvert(true);
  };

  const ouvrirEdition = () => {
    if (!detail) return;
    setForm({
      civilite: detail.civilite ?? '',
      prenom: detail.prenom ?? '',
      nom: detail.nom ?? '',
      dateNaissance: detail.dateNaissance ?? '',
      lieuNaissance: detail.lieuNaissance ?? '',
      nationalite: detail.nationalite ?? '',
      sexe: detail.sexe ?? '',
      email: detail.email ?? '',
      telephone: detail.telephone ?? '',
      adresse: detail.adresse ?? '',
      codePostal: detail.codePostal ?? '',
      ville: detail.ville ?? '',
      discipline: detail.discipline ?? '',
      commentaire: detail.commentaire ?? '',
    });
    setEnEdition(detail.id);
    setFormOuvert(true);
  };

  // Les contacts s'éditent en bloc : la liste est courte, et un envoi complet
  // évite les demi-listes quand deux personnes la touchent en même temps.
  const contactsEdites = useMemo(() => {
    if (contacts !== null) return contacts;
    const existants: any[] = detail?.contacts ?? [];
    return contactsInitiaux.map((modele) => {
      const trouve = existants.find((c) => c.role === modele.role);
      return trouve ? { ...modele, ...trouve } : { ...modele };
    });
  }, [contacts, detail]);

  const modifierContact = (index: number, patch: Record<string, string>) =>
    setContacts(contactsEdites.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const envoyerDocument = () => {
    if (selection === null) return;

    // Un bouton qui ne fait rien passe pour cassé : chaque refus se dit, à
    // côté du bouton, plutôt que de laisser l'utilisateur recliquer.
    const fichier = fichierRef.current?.fichier() ?? null;
    if (!fichier) {
      setRefusDepot('Choisissez d\'abord un fichier.');
      return;
    }
    if (typeDocument === '') {
      setRefusDepot('Dites de quel type de pièce il s\'agit.');
      return;
    }

    // Sous « Autre », une pièce sans nom devient introuvable : six mois plus
    // tard, « scan_0042.pdf » ne dit rien à personne.
    if (typeDocument === 'autre' && libelleDocument.trim() === '') {
      setRefusDepot("Précisez de quel document il s'agit.");
      return;
    }

    setRefusDepot(null);

    const formulaire = new FormData();
    formulaire.append('document', fichier);
    formulaire.append('type', typeDocument);
    if (libelleDocument.trim() !== '') formulaire.append('libelle', libelleDocument.trim());
    deposer.mutate(formulaire);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-primary-600" />
            {TITRES[onglet].titre}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{TITRES[onglet].aide}</p>
        </div>
        {peutGerer && onglet === 'repertoire' && (
          <button
            onClick={ouvrirCreation}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Nouvel adhérent
          </button>
        )}
      </div>


      {notice && (
        <div
          className={`mb-4 flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notice.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span>{notice.message}</span>
        </div>
      )}

      {/*
        Le tableau du trésorier n'a plus besoin de rappeler cet écran.

        Il renvoyait un identifiant pour qu'on sélectionne l'adhérent dans le
        volet de droite — ce qui obligeait à revenir ici, puis à retrouver la
        personne dans la liste. Chaque nom mène désormais à sa FICHE COMPLÈTE,
        qui répond aux trois questions qu'on se pose depuis ce tableau : qui
        c'est, comment le joindre, et ce qu'il doit.
      */}
      {onglet === 'cotisations' && <TableauCotisations />}

      {onglet === 'equipements' && <InventaireEquipements />}

      {onglet === 'equipes' && <EquipesClub />}

      {onglet === 'repertoire' && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ------------------------------ La liste ------------------------------ */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Nom, prénom, ville ou courriel"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            {recherche.trim().length === 1 && (
              <p className="text-xs text-gray-500">Encore une lettre et la recherche part.</p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              {(config?.disciplines ?? []).length > 1 && (
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                >
                  <option value="">Toutes les disciplines</option>
                  {(config?.disciplines ?? []).map((d: string) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={inactifs}
                  onChange={(e) => setInactifs(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600"
                />
                Afficher les inactifs
              </label>
            </div>
          </div>

          {registre.isPending ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : adherents.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
              {requeteRecherche
                ? 'Personne ne répond à cette recherche.'
                : 'Le registre est vide : le premier adhérent reste à inscrire.'}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 px-1">
                {adherents.length} adhérent{adherents.length > 1 ? 's' : ''}
              </p>
              {adherents.map((a) => (
                <button
                  key={a.id}
                  onClick={() => {
                    setSelection(a.id);
                    setContacts(null);
                  }}
                  className={`w-full text-left flex items-center gap-3 rounded-xl border p-3 transition ${
                    selection === a.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-primary-300'
                  }`}
                >
                  {a.photo ? (
                    <img src={a.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                      {initiales(a.prenom, a.nom)}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-gray-900 truncate">
                      {a.nomComplet}
                      {!a.actif && <span className="ml-2 text-xs text-gray-400">(inactif)</span>}
                    </span>
                    <span className="block text-xs text-gray-500 truncate">
                      {[a.categorie, a.age !== null ? `${a.age} ans` : null, a.discipline]
                        .filter(Boolean)
                        .join(' · ') || 'Sans catégorie'}
                    </span>
                  </span>
                  {a.documentsManquants?.length > 0 && (
                    <span
                      title={`Pièces manquantes : ${a.documentsManquants.join(', ')}`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-medium"
                    >
                      <AlertCircle className="w-3 h-3" />
                      {a.documentsManquants.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ------------------------------- La fiche ------------------------------ */}
        <div className="lg:col-span-3">
          {selection === null ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
              <UserRound className="w-8 h-8 mx-auto text-gray-300 mb-3" />
              Choisissez un adhérent pour voir sa fiche.
            </div>
          ) : fiche.isPending || !detail ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* En-tête de la fiche */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    {detail.photo ? (
                      <img src={detail.photo} alt="" className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <span className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-lg font-semibold">
                        {initiales(detail.prenom, detail.nom)}
                      </span>
                    )}
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900 truncate">{detail.nomComplet}</h2>
                      <p className="text-sm text-gray-500">
                        {[detail.categorie, detail.age !== null ? `${detail.age} ans` : null, detail.discipline]
                          .filter(Boolean)
                          .join(' · ') || 'Catégorie à préciser'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/*
                      LA FICHE COMPLÈTE, EN PREMIER DES TROIS.

                      Ce volet montre un tiers de ce que le club sait d'une
                      personne : état civil, contacts, pièces. Ses cotisations,
                      ses équipements, son équipe et ses convocations vivent
                      dans quatre autres écrans, et l'on y va en gardant le nom
                      en tête.

                      L'icône ouvre une page qui rassemble tout, rangé par
                      blocs, et qui s'imprime.

                      Elle vient AVANT « modifier » et « supprimer », et n'est
                      pas réservée à qui peut gérer : consulter n'est pas
                      modifier, et un entraîneur qui n'a que le droit de voir a
                      besoin de la lire autant que le président.
                    */}
                    <Link
                      to={`/adherents/${detail.id}`}
                      title="Ouvrir la fiche complète"
                      aria-label="Ouvrir la fiche complète"
                      className="p-2 rounded-lg border border-primary-200 text-primary-700 hover:bg-primary-50"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Link>

                    {peutGerer && (
                      <>
                      <button
                        onClick={ouvrirEdition}
                        title="Modifier la fiche"
                        className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Retirer ${detail.nomComplet} du registre actif ?`)) {
                            retirer.mutate(detail.id);
                          }
                        }}
                        title="Retirer du registre"
                        className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      </>
                    )}
                  </div>
                </div>

                {detail.documentsManquants?.length > 0 && (
                  <p className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Pièces obligatoires manquantes : {detail.documentsManquants.join(', ')}.</span>
                  </p>
                )}
              </div>

              {/* État civil */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">État civil et coordonnées</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  {[
                    ['Né(e) le', detail.dateNaissance],
                    ['À', detail.lieuNaissance],
                    ['Nationalité', detail.nationalite],
                    ['Sexe', detail.sexe],
                    ['Courriel', detail.email],
                    ['Téléphone', detail.telephone],
                    ['Adresse', detail.adresse],
                    ['Commune', [detail.codePostal, detail.ville].filter(Boolean).join(' ')],
                  ].map(([label, valeur]) => (
                    <div key={label as string}>
                      <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
                      <dd className="text-gray-800">{valeur || <span className="text-gray-300">—</span>}</dd>
                    </div>
                  ))}
                </dl>
                {detail.commentaire && (
                  <p className="mt-4 text-sm text-gray-600 whitespace-pre-line border-t border-gray-100 pt-3">
                    {detail.commentaire}
                  </p>
                )}
              </div>

              <LicenceAdherent adherent={detail} configuration={config} />

              <EquipementsAdherent adherent={detail} configuration={config} />

              {/* Contacts d'urgence */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary-600" />
                  Contacts d'urgence
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Les numéros qu'un encadrant compose sans avoir à chercher.
                </p>
                <div className="space-y-4">
                  {contactsEdites.map((contact, index) => (
                    <div key={contact.role} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                      <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5">
                        {contact.role === 'medecin' ? (
                          <Stethoscope className="w-3.5 h-3.5" />
                        ) : (
                          <UserRound className="w-3.5 h-3.5" />
                        )}
                        {libelleRole[contact.role] ?? contact.role}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          value={contact.nom ?? ''}
                          onChange={(e) => modifierContact(index, { nom: e.target.value })}
                          placeholder="Nom"
                          disabled={!peutGerer}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
                        />
                        <input
                          value={contact.telephone ?? ''}
                          onChange={(e) => modifierContact(index, { telephone: e.target.value })}
                          placeholder="Téléphone"
                          disabled={!peutGerer}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
                        />
                        {contact.role === 'medecin' ? (
                          <input
                            value={contact.lien ?? ''}
                            onChange={(e) => modifierContact(index, { lien: e.target.value })}
                            placeholder="Cabinet"
                            disabled={!peutGerer}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
                          />
                        ) : (
                          <LienParente
                            valeur={contact.lien ?? ''}
                            liens={liens}
                            desactive={!peutGerer}
                            onChange={(lien) => modifierContact(index, { lien })}
                          />
                        )}
                        <input
                          value={contact.email ?? ''}
                          onChange={(e) => modifierContact(index, { email: e.target.value })}
                          placeholder="Courriel"
                          disabled={!peutGerer}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
                        />
                        <input
                          value={contact.remarque ?? ''}
                          onChange={(e) => modifierContact(index, { remarque: e.target.value })}
                          placeholder="Remarque (allergie, traitement…)"
                          disabled={!peutGerer}
                          className="sm:col-span-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {peutGerer && (
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      onClick={() => enregistrerContacts.mutate(contactsEdites)}
                      disabled={enregistrerContacts.isPending}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
                    >
                      {enregistrerContacts.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                      Enregistrer les contacts
                    </button>
                    {contacts !== null && (
                      <button onClick={() => setContacts(null)} className="text-sm text-gray-500 hover:text-gray-700">
                        Annuler
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Pièces au dossier */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-primary-600" />
                  Pièces au dossier
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Certificat médical, autorisation parentale, assurance : ce que le club doit pouvoir ressortir.
                </p>

                {(detail.documents ?? []).length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune pièce déposée.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {detail.documents.map((doc: any) => (
                      <li key={doc.id} className="flex items-center gap-3 py-2">
                        <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 min-w-0 text-sm text-gray-800 hover:text-primary-700 truncate"
                        >
                          {doc.libelle}
                        </a>
                        {doc.valableJusquau && (
                          <span
                            className={`text-xs rounded-full px-2 py-0.5 ${
                              doc.perime ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {doc.perime ? 'Périmé le ' : "Jusqu'au "}
                            {doc.valableJusquau}
                          </span>
                        )}
                        {peutGerer && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Supprimer « ${doc.libelle} » ?`)) retirerDocument.mutate(doc.id);
                            }}
                            title="Supprimer la pièce"
                            className="p-1.5 rounded text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {peutGerer && (
                  <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                    <select
                      value={typeDocument}
                      onChange={(e) => setTypeDocument(e.target.value)}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                    >
                      <option value="">Type de pièce…</option>
                      {(config?.documents ?? []).map((d: any) => (
                        <option key={d.code} value={d.code}>
                          {d.libelle}
                        </option>
                      ))}
                    </select>
                    {typeDocument === 'autre' && (
                      <input
                        value={libelleDocument}
                        onChange={(e) => setLibelleDocument(e.target.value)}
                        placeholder="De quel document s'agit-il ? *"
                        className="px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-sm"
                      />
                    )}
                    <ChoixFichier ref={fichierRef} libelle="Choisir la pièce…" />
                    <button
                      onClick={envoyerDocument}
                      disabled={deposer.isPending}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50 disabled:opacity-60"
                    >
                      {deposer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Déposer
                    </button>

                    {refusDepot && (
                      <p className="w-full text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                        {refusDepot}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ------------------------------ Le formulaire ----------------------------- */}
      {formOuvert && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl my-8 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {enEdition !== null ? 'Modifier la fiche' : 'Nouvel adhérent'}
              </h2>
              <button onClick={() => setFormOuvert(false)} className="p-1.5 rounded hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select
                value={form.civilite}
                onChange={(e) => setForm({ ...form, civilite: e.target.value })}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
              >
                <option value="">Civilité</option>
                <option value="M.">M.</option>
                <option value="Mme">Mme</option>
              </select>
              <select
                value={form.sexe}
                onChange={(e) => setForm({ ...form, sexe: e.target.value })}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
              >
                <option value="">Catégorie de jeu</option>
                <option value="Masculin">Masculin</option>
                <option value="Féminin">Féminin</option>
                <option value="Mixte">Mixte</option>
              </select>
              {[
                ['prenom', 'Prénom *'],
                ['nom', 'Nom *'],
                ['dateNaissance', 'Date de naissance'],
                ['lieuNaissance', 'Lieu de naissance'],
                ['nationalite', 'Nationalité'],
                ['email', 'Courriel'],
                ['telephone', 'Téléphone'],
                ['codePostal', 'Code postal'],
                ['ville', 'Ville'],
              ].map(([champ, label]) => (
                <input
                  key={champ}
                  type={champ === 'dateNaissance' ? 'date' : champ === 'email' ? 'email' : 'text'}
                  value={(form as any)[champ]}
                  onChange={(e) => setForm({ ...form, [champ]: e.target.value })}
                  placeholder={label}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                />
              ))}
              <input
                value={form.adresse}
                onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                placeholder="Adresse"
                className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-300 text-sm"
              />
              <select
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-300 text-sm"
              >
                <option value="">Discipline…</option>
                {(config?.disciplines ?? []).map((d: string) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <textarea
                value={form.commentaire}
                onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
                placeholder="Remarques du bureau"
                rows={3}
                className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-300 text-sm"
              />
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setFormOuvert(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Annuler
              </button>
              <button
                onClick={() => enregistrer.mutate(form)}
                disabled={enregistrer.isPending || !form.prenom.trim() || !form.nom.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
              >
                {enregistrer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {enEdition !== null ? 'Enregistrer' : 'Inscrire au registre'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Le lien de parenté : une liste, et un champ libre quand rien ne convient.
 *
 * Un club accueille des familles d'accueil, des tuteurs, des grands-parents
 * qui élèvent l'enfant : la liste couvre les cas courants sans prétendre les
 * couvrir tous.
 */
function LienParente({
  valeur,
  liens,
  desactive,
  onChange,
}: {
  valeur: string;
  liens: Record<string, string>;
  desactive: boolean;
  onChange: (lien: string) => void;
}) {
  const connus = Object.values(liens).filter((l) => l !== 'Autre');
  const libre = valeur !== '' && !connus.includes(valeur);

  return (
    <div className="flex gap-2">
      <select
        value={libre ? '__autre__' : valeur}
        onChange={(e) => onChange(e.target.value === '__autre__' ? ' ' : e.target.value)}
        disabled={desactive}
        className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100"
      >
        <option value="">Lien de parenté…</option>
        {connus.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
        <option value="__autre__">Autre…</option>
      </select>
      {libre && (
        <input
          autoFocus
          value={valeur.trim()}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Précisez *"
          disabled={desactive}
          className="w-32 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-sm disabled:bg-gray-100"
        />
      )}
    </div>
  );
}
